import React from "react";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AddIcon from "@mui/icons-material/Add";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";


function isoToDayjs(dueDateIso) {
  if (!dueDateIso) return null;
  // Backend serializes as YYYY-MM-DD; treat as a local-tz date.
  const parsed = dayjs(dueDateIso);
  return parsed.isValid() ? parsed : null;
}


// Shared sx config so all date inputs in the row share the same compact size.
// Border stays MUI-default at rest/hover; focused state shows violet to match
// the rest of the app's primary accent.
const DATE_PICKER_SLOT_PROPS = {
  textField: {
    size: "small",
    sx: {
      width: 170,
      flexShrink: 0,
      "& .MuiOutlinedInput-root": {
        backgroundColor: "white",
        height: 36,
        fontSize: "13px",
        "&.Mui-focused fieldset": { borderColor: "#6d28d9" },
      },
    },
  },
  popper: {
    sx: {
      "& .MuiPickersDay-root.Mui-selected": {
        backgroundColor: "#6d28d9",
      },
    },
  },
};


// Match the visual width of the add-row's plus button so the date pickers
// in both rows line up vertically.
const ICON_BUTTON_SX = { width: 32, height: 32, flexShrink: 0 };


function ActionItemsSection(props) {
  const items = props.actionItems || [];
  const [draftText, setDraftText] = React.useState("");
  const [draftDueDate, setDraftDueDate] = React.useState(null); // dayjs | null
  const [isAdding, setIsAdding] = React.useState(false);

  const handleAdd = async () => {
    const text = draftText.trim();
    if (!text) return;
    setIsAdding(true);
    try {
      // Guard against in-progress keyboard entry — MUI's DatePicker hands us
      // an invalid Dayjs instance while the user is mid-typing, and
      // format("YYYY-MM-DD") on that produces "Invalid Date" which the
      // backend rejects with a 400.
      const dueDateIso =
        draftDueDate && draftDueDate.isValid()
          ? draftDueDate.format("YYYY-MM-DD")
          : null;
      const success = await props.onAdd(text, dueDateIso);
      if (success) {
        setDraftText("");
        setDraftDueDate(null);
      }
    } finally {
      setIsAdding(false);
    }
  };

  const editsDisabled = !!props.editsDisabled;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Action items</h3>

        {items.length === 0 && (
          <p className="text-xs italic text-slate-500 mb-3">
            {editsDisabled
              ? "No action items were captured for this check-in."
              : "Capture commitments below. Open ones will carry forward to the next check-in."}
          </p>
        )}

        <ul className="space-y-2 mb-3">
          {items.map((item) => {
            const isDone = !!item.completed_at;
            return (
              <li
                key={item.uuid}
                className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2"
              >
                <Tooltip
                  title={isDone ? "Mark not done" : "Mark done"}
                  arrow
                  placement="top"
                >
                  <Checkbox
                    checked={isDone}
                    onChange={() => props.onToggle(item.uuid)}
                    size="small"
                    sx={{
                      padding: 0,
                      "&.Mui-checked": { color: "#6d28d9" },
                    }}
                  />
                </Tooltip>

                <TextField
                  value={item.text}
                  onChange={(evt) =>
                    props.onEditTextLocal(item.uuid, evt.target.value)
                  }
                  onBlur={() => props.onEditTextCommit(item.uuid)}
                  fullWidth
                  multiline
                  size="small"
                  variant="standard"
                  disabled={editsDisabled}
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      fontSize: "14px",
                      color: isDone ? "#94a3b8" : "#0f172a",
                      textDecoration: isDone ? "line-through" : "none",
                      // Standard variant's default 4px/5px asymmetric padding
                      // makes the first line render slightly above the row's
                      // geometric center. Equalize to 8px so single-line text
                      // sits exactly mid-row alongside the DatePicker (36px),
                      // and wrapped content grows the row symmetrically.
                      "& textarea": {
                        paddingTop: "8px",
                        paddingBottom: "8px",
                      },
                    },
                  }}
                />

                <DatePicker
                  value={isoToDayjs(item.due_date)}
                  onChange={(newDate) => {
                    // MUI fires onChange for every keystroke during keyboard
                    // entry, including transient invalid Dayjs instances.
                    // Treat null (cleared) as a real "no due date" update,
                    // but swallow invalids so we don't ship "Invalid Date"
                    // strings to the backend and trigger 400s mid-typing.
                    if (newDate === null) {
                      props.onEditDueDate(item.uuid, null);
                      return;
                    }
                    if (!newDate.isValid()) return;
                    props.onEditDueDate(item.uuid, newDate.format("YYYY-MM-DD"));
                  }}
                  disabled={editsDisabled}
                  slotProps={DATE_PICKER_SLOT_PROPS}
                />

                <Tooltip title="Delete" arrow placement="top">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => props.onDelete(item.uuid)}
                      disabled={editsDisabled}
                      sx={ICON_BUTTON_SX}
                    >
                      <DeleteOutlinedIcon
                        style={{ fontSize: "18px" }}
                        className={editsDisabled ? "text-slate-300" : "text-slate-500 hover:text-rose-600"}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              </li>
            );
          })}
        </ul>

        {!editsDisabled && (
          <div className="flex gap-2 items-center bg-white border-2 border-dashed border-slate-300 rounded-lg p-2">
            <TextField
              placeholder="Add an action item…"
              value={draftText}
              onChange={(evt) => setDraftText(evt.target.value)}
              onKeyDown={(evt) => {
                if (evt.key === "Enter" && !evt.shiftKey) {
                  evt.preventDefault();
                  handleAdd();
                }
              }}
              fullWidth
              size="small"
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: { fontSize: "14px" },
              }}
            />
            <DatePicker
              value={draftDueDate}
              onChange={(newDate) => setDraftDueDate(newDate)}
              slotProps={DATE_PICKER_SLOT_PROPS}
            />
            <Tooltip title="Add" arrow placement="top">
              <span>
                <IconButton
                  size="small"
                  onClick={handleAdd}
                  disabled={!draftText.trim() || isAdding}
                  sx={ICON_BUTTON_SX}
                >
                  <AddIcon
                    style={{ fontSize: "20px" }}
                    className={
                      !draftText.trim() ? "text-slate-300" : "text-violet-600"
                    }
                  />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        )}
      </div>
    </LocalizationProvider>
  );
}

export default ActionItemsSection;
